/**
 * Geometry of the heads-up tile row (#131): how many whole tiles it shows,
 * and what is off to either side once it has been scrolled.
 *
 * Kept free of the DOM so the rules can be tested with real screen sizes;
 * `VideoTileGrid` measures the tiles and feeds the numbers in.
 */

/**
 * How many whole tiles the row shows: as many as fit in `availableWidth`, but
 * at least one, at most `cap`, and never more than there are tiles.
 *
 * `availableWidth` is the free width (the strip between any open drawers)
 * less the row's 2rem margin, the same bound the `.video-tile-row` rule in
 * `index.css` applies. Until the tiles have been measured (`tileWidth` 0),
 * the row assumes the cap.
 */
export function visibleTileCount({
  availableWidth,
  tileWidth,
  gap,
  cap,
  total
}: {
  availableWidth: number;
  tileWidth: number;
  gap: number;
  cap: number;
  total: number;
}): number {
  const limit = Math.max(1, Math.min(cap, total));
  if (!(tileWidth > 0)) return limit;

  // n tiles need n * tile + (n - 1) * gap. The epsilon keeps an exact fit
  // from rounding down to one tile fewer.
  const fit = Math.floor((availableWidth + gap) / (tileWidth + gap) + 1e-6);
  return Math.max(1, Math.min(limit, fit));
}

/**
 * Index of the first tile in view. The row snaps to whole tiles, so between
 * two snap points (mid-scroll) this is the nearer one.
 */
export function firstTileInView(scrollLeft: number, stride: number): number {
  return stride > 0 ? Math.max(0, Math.round(scrollLeft / stride)) : 0;
}

/** What the row's controls need to know about its scroll position. */
export interface RowWindow {
  /** First tile in view, clamped to the last position the row can reach. */
  start: number;
  /** Something to the left: show the back arrow. */
  canScrollBack: boolean;
  /** Something to the right: show the forward arrow. */
  canScrollForward: boolean;
  /** Tiles off to the right of the row: the `+N` chip. */
  hiddenAfter: number;
}

/** The window of `visible` tiles starting at `start`, out of `total`. */
export function rowWindow({
  start,
  visible,
  total
}: {
  start: number;
  visible: number;
  total: number;
}): RowWindow {
  const lastStart = Math.max(0, total - visible);
  const clamped = Math.min(Math.max(0, start), lastStart);
  return {
    start: clamped,
    canScrollBack: clamped > 0,
    canScrollForward: clamped < lastStart,
    hiddenAfter: lastStart - clamped
  };
}
